import Layout from "../../components/layout";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import nextI18NextConfig from "../../next-i18next.config";
import Accordion from "../../components/v2/accordion/accordion";
import Link from "../../components/Link";
import CallToActionSmall from "../../components/v2/call-to-action-sm";
import { GetStaticProps } from "next";
import ComparisonForm from "../../components/v2/form/comparison-form";
import { useTranslation } from "next-i18next";
import Head from "next/head";
import dynamic from "next/dynamic";
import { useState } from "react";
import "../../styles/scss/pricing.module.scss";
import '../../styles/scss/_faq-section.module.scss';
import "../../styles/scss/solutions.module.scss";
import TrustedCompaniesV2 from "../../components/v2/trusted-companies-v2";
import VendorComparisonGrid from "../../components/v2/comparison/vendor-comparison-grid";
import VendorComparisonTable from "../../components/v2/comparison/vendor-comparison-table";
import {
    NEWRELIC_COMPARISON_SECTIONS,
    NEWRELIC_FEATURE_TABLE_ROWS,
    NEWRELIC_SWITCH_STATS,
} from "../../components/v2/comparison/newrelic-comparison-data";
import VendorSwitchStats from "../../components/v2/comparison/vendor-switch-stats";
import CallToActionDocsLink from "../../components/v2/blog/call-to-action-docs-link";
import CardWithIcon from "../../components/v2/cards/card-with-icon";
import ComparisonVideoTestimonial from "../../components/v2/comparison/comparison-video-testimonial";
import { AIK8sRootIconDark, AutoFixIconDark, OpsAIDarkIcon, AIDashboardIconDark, AIAlertIconDark, ZeroReinstrumentationIcon, IncrementalAdoptionIcon, DataContinuityIcon } from "../../components/v2/icons-v2";

const Dialog = dynamic(() => import("../../components/v2/dialog"), {
    ssr: false,
});

export default function NewRelicAlternative({ }) {
    const { t } = useTranslation('common');
    const data = t('comparison.newrelic', { returnObjects: true, defaultValue: {} }) as any;

    const [testimonialVideoOpen, setTestimonialVideoOpen] = useState(false);
    const [testimonialVideoSrc, setTestimonialVideoSrc] = useState<string | null>(null);

    const handleTestimonialVideoOpen = (src: string) => {
        setTestimonialVideoSrc(src);
        setTestimonialVideoOpen(true);
    };

    const formData = [
        {
            id: 1,
            header: "Zero Re-instrumentation",
            icon: <ZeroReinstrumentationIcon />,

        },
        {
            id: 2,
            header: "Incremental Adoption",
            icon: <IncrementalAdoptionIcon />,

        },
        {
            id: 3,
            header: "100% Data Continuity",
            icon: <DataContinuityIcon />,

        },
    ]

    const faqs = [
        {
            id: 1,
            header: "What's the main difference between Middleware and New Relic's pricing models?",
            text: (
                <p>Middleware uses a single transparent meter — pay for the data size you send. New Relic uses three meters at once: per-GB ingest, per-user, and Compute Capacity Units, plus add-ons like Data Plus.</p>
            ),
        },
        {
            id: 2,
            header:
                "Will I pay extra for users on Middleware?",
            text: (
                <p>No. Middleware has no per-user fees. Add as many teammates as you want.</p>
            ),
        },
        {
            id: 3,
            header:
                "Is Middleware really free to try?",
            text: (
                <p>Yes. Use log monitoring, infrastructure monitoring, APM, and every other feature free for 14 days. No credit card, no time limit beyond the trial.</p>
            ),
        },
        {
            id: 4,
            header: "How does Middleware compare to New Relic on data ingest cost?",
            text: (
                <p>Middleware charges $0.30/GB for metrics, logs, and traces after the trial. New Relic charges $0.30/GB on Standard, $0.40/GB on the Original data option, and $0.60/GB on Data Plus after the 100 GB free tier. For 1 TB of ingest a month, customers save roughly 22% on raw ingest alone — and more once user fees and CCUs are factored in.</p>
            ),
        },
        {
            id: 5,
            header: "Will I save money switching from New Relic?",
            text: (
                <p>Most customers see 50–75% reductions. Send us your New Relic invoice and we'll show you the exact number.</p>
            ),
        },
        {
            id: 6,
            header: "What happens if my data spikes? Will my bill 10x like it can on New Relic?",
            text: (
                <p>No. Middleware bills on actual usage, not peak, and the Ingestion Control feature lets you drop high-volume telemetry sources in two clicks before they hit your bill. Custom events and custom metrics are included — not metered separately.</p>
            ),
        },
        {
            id: 7,
            header: "Is Middleware OpenTelemetry-native?",
            text: (
                <p>Yes. OTel is the foundation, not an afterthought. Your instrumentation stays portable across vendors — no proprietary agent lock-in, no NRQL dependency.</p>
            ),
        },
        {
            id: 8,
            header: "Do I need to learn a new query language to use Middleware?",
            text: (
                <p>No. Middleware's UI is designed so most workflows don't require any query language. For advanced cases, the query interface is built around OTel attributes you already know.</p>
            ),
        },
        {
            id: 9,
            header: "Can I deploy Middleware on-premises or BYOC?",
            text: (
                <p>Yes. Cloud, on-prem, BYOC (bring your own cloud), or hybrid — your choice, for cost, security, or compliance reasons.</p>
            ),
        },
        {
            id: 10,
            header: "What happens to my New Relic dashboards when I migrate?",
            text: (
                <p>Middleware has its own dashboard builder, and our team provides onboarding support to help you recreate your critical New Relic dashboards in Middleware during migration.</p>
            ),
        },
        {
            id: 11,
            header: "Can I keep my existing New Relic OTel instrumentation?",
            text: (
                <p>Yes. If you're already sending OpenTelemetry data, you can repoint your collectors at Middleware and start ingesting immediately. No re-instrumentation required.</p>
            ),
        },
    ];



    const softwareApplicationSchema = {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "Middleware Observability Platform",
        "url": "https://middleware.io/",
        "applicationCategory": "DeveloperApplication",
        "operatingSystem": "Web",
        "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.7",
            "reviewCount": "320"
        }
    };

    const webPageSchema = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "@id": "https://middleware.io/comparison/newrelic-alternative/",
        "name": "Middleware vs New Relic – New Relic Alternative",
        "description": "Compare Middleware and New Relic across observability features, pricing, and ease of use.",
        "mainEntity": {
            "@type": "ItemList",
            "itemListOrder": "Unordered",
            "numberOfItems": 2,
            "itemListElement": [
                {
                    "@type": "ListItem",
                    "position": 1,
                    "item": {
                        "@type": "SoftwareApplication",
                        "name": "Middleware Observability Platform",
                        "url": "https://middleware.io/",
                        "applicationCategory": "DeveloperApplication",
                        "operatingSystem": "Web"
                    }
                },
                {
                    "@type": "ListItem",
                    "position": 2,
                    "item": {
                        "@type": "SoftwareApplication",
                        "name": "New Relic",
                        "url": "https://newrelic.com/",
                        "applicationCategory": "DeveloperApplication",
                        "operatingSystem": "Web"
                    }
                }
            ]
        },
        "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD",
            "url": "https://middleware.io/"
        }
    };


    return (
        <>
            <Head>
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify(softwareApplicationSchema)
                    }}
                />
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify(webPageSchema)
                    }}
                />
            </Head>
            <Layout
                isHomePage={false}
                pageTitle={"New Relic Alternative: Compare Middleware vs New Relic"}
                ogImage={"/images/comparison/newrelic-og.webp"}
                uri={String(data?.uri ?? "/comparison/newrelic-alternative/")}
                metaTitle={"Explore why engineering teams choose Middleware over New Relic. Compare pricing, OpenTelemetry-native observability, logs, metrics, traces, and AI insights."}
                metaDesc={"New Relic Alternative: Compare Middleware vs New Relic"}
                metaRobotsNoindex={String(data?.metaRobotsNoindex ?? "index")}
                metaRobotsNofollow={String(data?.metaRobotsNofollow ?? "follow")}
            >

                <div className={"mw-vs-datadog solutions"}>
                    <section className={`hero-section solutions-child-page`}>
                        <div className={'container'}>
                            <h1>The Better New Relic<span className='text-gradient'> Alternative</span><br /> Full-Stack Observability with an SRE Agent Built In</h1>
                            <p className="mb-4">Tired of per-user licensing, Compute Capacity Units, NRQL lock-in, and an AI that stops at the summary? Switch to Middleware, a unified, OpenTelemetry-native observability platform with predictable pricing, one-minute setup that comes with OpsAI, an SRE Agent that ships the fix as a pull request.</p>

                            <div className={"d-flex"}>
                                <Link href={"/demo-request/"} target={"_blank"}
                                    className={"sign-up ahref-btn primary"}>Request a Demo</Link>
                                <Link href={'#comparison-mw'} className={"sign-up ahref-btn secondary"}>See Comparison</Link>
                            </div>
                        </div>
                    </section>


                    <TrustedCompaniesV2
                        className="home-page-customer"
                        paragraph="Trusted By The Best Teams From"
                    />

                    <VendorComparisonGrid
                        sections={NEWRELIC_COMPARISON_SECTIONS}
                        competitor={{
                            name: "New Relic",
                            logoSrc: "/images/comparison/newrelic-border-logo.svg",
                            logoAlt: "New Relic Logo",
                            cellLogoSrc: "/images/comparison/newrelic-logo.svg",
                            headerLogoWidth: 140,
                            headerLogoHeight: 42,
                        }}
                    />

                    <CallToActionDocsLink
                        title={'Point existing New Relic alerts to Middleware & switch gradually'}
                        docsLink="https://docs.middleware.io"
                    />

                    <section className="ai-native-features">
                        <div className="container">
                            <h2>The first observability platform built around AI, <br />
                                not bolted onto it</h2>

                            <section className={'mw-card career'}>
                                <div className={'d-flex card-items'}>
                                    <CardWithIcon
                                        icon={<AIDashboardIconDark />}
                                        title={"AI Dashboards"}
                                        description={
                                            "Describe what you want in plain English. Middleware builds the dashboard, picks the charts, and writes the queries. No PromQL, no custom query language, no template gallery to comb through."
                                        }
                                    />
                                    <CardWithIcon
                                        icon={<AIAlertIconDark />}
                                        title={"AI Alerts"}
                                        description={
                                            "Alerts that learn what \"normal\" looks like for each service and surface only the deviations that matter. Anomaly detection that doesn't drown your on-call rotation in noise."
                                        }
                                    />
                                    <CardWithIcon
                                        icon={<OpsAIDarkIcon />}
                                        title={"OpsAI · SRE Agent"}
                                        description={
                                            "An AI SRE that pinpoints root cause, identifies the offending line of code, and opens a pull request with the fix while you're still reading the alert. Auto-resolves issues with 80% accuracy."
                                        }
                                    />
                                    <CardWithIcon
                                        icon={<AIK8sRootIconDark />}
                                        title={"AI K8s Root Cause Finder"}
                                        description={
                                            "Auto-structuring, severity detection, and pattern recognition built into log ingestion. No manual regular expressions, no parsing pipelines to maintain."
                                        }
                                    />
                                    <CardWithIcon
                                        icon={<AutoFixIconDark />}
                                        title={"Auto-Fix · 60% Time Saved"}
                                        description={
                                            "Auto-fix across infrastructure and application layers with 80% accuracy. Engineers spend 60% less time on issue resolution — and the rest on shipping product."
                                        }
                                    />

                                </div>
                            </section>
                        </div>
                    </section>

                    <div className="customer-stories-comparison">
                        <div className="container">
                            <div className="section-tag testimonial">
                                Testimonials
                            </div>
                            <h2><span className="text-gradient">Customer </span> Stories</h2>


                            <div className="d-flex">
                                <div className="customer-story-card">
                                    <img src="/images/trusted-company/aceturtle.svg" alt="Aceturtle" />
                                    <p>Middleware has played a very good role in transforming our observability and application performance. For instance, we reduced our total observability costs by 50%.</p>
                                    <Link href="/customers/aceturtle/" className="read-more">
                                        Read the story
                                    </Link>
                                    <div className="customer-author">
                                        <p className="name">Vivek Gupta</p>
                                        <p>Founder & CEO</p>
                                    </div>
                                </div>

                                <div className="customer-story-card">
                                    <img src="/images/trusted-company/hotplate.svg" alt="Hotplate" />
                                    <p>We're heavy users of RUM and have tried many tools before discovering Middleware. We've been using OpsAI for the past few weeks, and it has automatically resolved about 60% of our production issues.</p>
                                    <Link href="/customers/hotplate/" className="read-more">
                                        Read the story
                                    </Link>
                                    <div className="customer-author">
                                        <p className="name">Rangaraj Tirumala</p>
                                        <p>Founding Engineer at Hotplate</p>
                                    </div>
                                </div>

                                <ComparisonVideoTestimonial onVideoOpen={handleTestimonialVideoOpen} />
                            </div>

                        </div>
                    </div>

                    <VendorComparisonTable
                        rows={NEWRELIC_FEATURE_TABLE_ROWS}
                        competitor={{
                            name: "New Relic",
                            logoSrc: "/images/comparison/newrelic-logo.svg",
                            logoAlt: "New Relic Logo",
                            cellLogoSrc: "/images/comparison/newrelic-logo.svg",
                            cellLogoWidth: 90,
                            cellLogoHeight: 22,
                            headerLogoWidth: 140,
                            headerLogoHeight: 42,
                        }}
                    />

                    <VendorSwitchStats content={NEWRELIC_SWITCH_STATS} />

                    <ComparisonForm title={"You Don't Have to Rip Out New Relic on Day One"} label={"New Relic"} formId={"f9036b30-a2b9-4c3d-b4bf-46761086a9be"} description={"Already running New Relic Alerts? Forward it to OpsAI and let it investigate & fix incidents against your existing stack without re-instrumentation"} isDatadog={true} formDataComparison={formData} docsLink="https://docs.middleware.io" />

                    <section className={"faq-section"}>
                        <div className={"container"}>
                            <h2>{String(data?.faq?.title ?? 'FAQs')}</h2>
                            <p>{String(data?.faq?.subtitle ?? '')}</p>
                            <div className={"faq-item"}>
                                <Accordion faqs={faqs} showGroups={true} />
                            </div>
                        </div>
                    </section>

                    <CallToActionSmall />

                </div>
            </Layout>

            <Dialog
                isOpen={testimonialVideoOpen}
                position="center"
                onClose={() => {
                    setTestimonialVideoOpen(false);
                    setTestimonialVideoSrc(null);
                }}
                size="md"
                verticalCenter={true}
            >
                <div style={{ textAlign: "center" }}>
                    {testimonialVideoSrc ? (
                        <video
                            key={testimonialVideoSrc}
                            playsInline
                            preload="none"
                            autoPlay
                            width="100%"
                            height={450}
                            controls={true}
                        >
                            <source src={testimonialVideoSrc} type="video/mp4" />
                        </video>
                    ) : null}
                </div>
            </Dialog>
        </>
    );
}

export const getStaticProps: GetStaticProps = async (ctx) => {
    const translations = await serverSideTranslations(
        ctx?.locale ?? "en",
        ["common", "header", "footer"],
        nextI18NextConfig,
    );
    return {
        props: {
            ...translations,
            pageTitle: "New Relic Alternative: Compare Middleware vs New Relic",
            description: "Explore why engineering teams choose Middleware over New Relic. Compare pricing, OpenTelemetry-native observability, logs, metrics, traces, and AI insights.",
            ogImage: "/images/comparison/newrelic-og.webp",
            uri: "/comparison/newrelic-alternative/",
            metaTitle: "New Relic Alternative: Compare Middleware vs New Relic",
            metaDesc: "Explore why engineering teams choose Middleware over New Relic. Compare pricing, OpenTelemetry-native observability, logs, metrics, traces, and AI insights.",
            metaRobotsNoindex: "index",
            metaRobotsNofollow: "follow",
        },
    }
}
